// Menus and tooltips. Radix provides keyboard navigation, focus return,
// collision-aware positioning and the ARIA roles; the look comes from the
// workspace tokens (.ws-menu, .ws-tip).
import type { ReactElement, ReactNode } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as Tooltip from "@radix-ui/react-tooltip";

export const TipProvider = Tooltip.Provider;

export function Menu({ trigger, children, label, align = "end" }: {
  trigger: ReactElement; children: ReactNode; label: string; align?: "start" | "end";
}) {
  return <DropdownMenu.Root>
    <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
    <DropdownMenu.Portal>
      <DropdownMenu.Content className="ws-menu" aria-label={label} align={align} sideOffset={6} collisionPadding={8}>
        {children}
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  </DropdownMenu.Root>;
}

/** A menu entry; pass `asChild` with a <Link> or <a> to navigate or download. */
export function MenuItem(props: DropdownMenu.DropdownMenuItemProps) {
  return <DropdownMenu.Item {...props} className={`ws-menu-item ${props.className || ""}`} />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <DropdownMenu.Label className="ws-menu-label">{children}</DropdownMenu.Label>;
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="ws-menu-sep" />;
}

/** A short label shown on hover and keyboard focus. `disabled` renders the child alone. */
export function Tip({ label, children, side = "top", disabled = false }: {
  label: ReactNode; children: ReactElement; side?: "top" | "right" | "bottom" | "left"; disabled?: boolean;
}) {
  if (disabled) return children;
  return <Tooltip.Root>
    <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content className="ws-tip" side={side} sideOffset={6} collisionPadding={8}>{label}</Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Root>;
}
